import { RadioButton } from 'components/core/fields/RadioButton'
import { RadioButtonGroup } from 'components/core/fields/RadioButtonGroup'
import React from 'react'
import { FormScreen } from 'components/screen-templates/FormScreen'
import * as Yup from 'yup'
import { onBackClickCallback, onNextClickType } from 'components/types'
import { Conditions, Quizzes } from '../../redux/quiz/types'
import { ContextAwareText } from '../core/text/ContextAwareText'
import { InputField } from '../core/fields/InputField'
import { evaluateCondition } from '../utils'
import { Answer } from '../../redux/quiz/slice'
import { DSSurface } from '@zoe/ds-web'
import styled, { keyframes } from 'styled-components'
import { ZiggieAsk } from '../core/ziggie/ZiggieAsk'
import { GifName } from '../../data/gifs'

type SortStrategy = 'random' | 'fixed'
type Option = {
  label: string
  value: string
  fixed?: boolean
  showWhen?: Conditions
  playback?: string
}

export type SelectScreenProps = {
  title: string
  description?: string
  topImage?: string
  topImageAlt?: string
  gif?: GifName
  topImageAspectRatio?: string
  topImageBleed?: boolean
  buttonTitle?: string
  questionKey: string
  options: Option[]
  hideBackLink?: boolean
  includeWeightGoalAnswers?: boolean
  order?: SortStrategy
  showTextInputWhen?: string
  textInputPlaceholder?: string
  textInputQuestionKey?: string
  directNavigation?: boolean
  columns?: number
  showArrow?: boolean
  subtitleAboveTitle?: boolean
  // Same copy, shown with Ziggie: Ziggie asks the title from a speech bubble
  // (the description isn't shown), and a selected option's playback replaces it.
  withZiggie?: { gif: GifName; typewriter?: boolean }
}

type Props = SelectScreenProps & {
  onNextClick: onNextClickType
  onBackClick: onBackClickCallback
  conditionValues: Required<Conditions>
  quiz: Quizzes
  goalAnswersToBeSubmitted: Answer[] | undefined
}

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const PlaybackWrapper = styled.div`
  margin-bottom: var(--grid-16);
  animation: ${slideUp} 0.3s ease-out;
`

const getVisibleOptions = (options: Option[], conditionValues: Required<Conditions>): Option[] => {
  return options.filter((option) => !option.showWhen || evaluateCondition(option.showWhen, conditionValues))
}

const sortOptions = (options: Option[], order: SortStrategy): Option[] => {
  if (order === 'random') {
    const nullifiedOptions = [...options].map((option) => (option.fixed ? option : null))
    const randomizedOptions = options.filter((option) => option && !option.fixed).sort(() => 0.5 - Math.random())
    return nullifiedOptions.map((option) => {
      return option === null ? (randomizedOptions.shift() as Option) : option
    })
  }
  return options
}

export const SelectScreen: React.FC<Props> = (props: Props) => {
  const previousAnswer = (props.quiz as Record<string, unknown>)?.[props.questionKey]
  const [selected, setSelected] = React.useState<string>(typeof previousAnswer === 'string' ? previousAnswer : '')
  const visibleOptions = getVisibleOptions(props.options, props.conditionValues)
  const sortedOptions = sortOptions(visibleOptions, props.order ?? 'fixed')
  const hasPlaybacks = sortedOptions.some((option) => option.playback)

  const selectedPlayback = sortedOptions.find((option) => option.value === selected)?.playback

  const handleSelected = (value: string) => {
    setSelected(value)
    if (props.directNavigation) {
      props.onNextClick({ [props.questionKey]: value }, props.goalAnswersToBeSubmitted)
    }
  }

  const options = sortedOptions.map((option, index) => (
    <React.Fragment key={index}>
      <RadioButton
        clicked={handleSelected}
        name={props.questionKey}
        id={option.value}
        value={option.value}
        label={option.label}
        showArrow={props.showArrow}
      />
      {props.showTextInputWhen === selected && props.textInputQuestionKey && option.value === selected && (
        <div style={{ marginBottom: 'var(--grid-16)' }}>
          <InputField
            name={props.textInputQuestionKey}
            type="text"
            placeholder={props.textInputPlaceholder ?? 'Please specify'}
          />
        </div>
      )}
    </React.Fragment>
  ))

  const playbackElement =
    hasPlaybacks && selectedPlayback && !props.withZiggie ? (
      <PlaybackWrapper key={selected}>
        <DSSurface surfaceColor="teal-medium" radius={12} padding={16}>
          <ContextAwareText text={selectedPlayback} quiz={props.quiz} />
        </DSSurface>
      </PlaybackWrapper>
    ) : null

  return (
    <FormScreen
      topImage={props.topImage}
      topImageAlt={props.topImageAlt}
      gif={props.gif}
      topImageAspectRatio={props.topImageAspectRatio}
      topImageBleed={props.topImageBleed}
      screenTitle={<ContextAwareText text={props.title} quiz={props.quiz} />}
      description={props.description ? <ContextAwareText text={props.description} quiz={props.quiz} /> : null}
      subtitleAboveTitle={props.subtitleAboveTitle}
      buttonText={props.buttonTitle ?? 'Continue'}
      footerSlot={playbackElement}
      questionSlot={
        props.withZiggie && (
          <ZiggieAsk
            gif={props.withZiggie.gif}
            text={selectedPlayback ?? props.title}
            quiz={props.quiz}
            typewriter={props.withZiggie.typewriter}
            subhead={props.description}
          />
        )
      }
      hideButton={props.directNavigation}
      form={{
        body: (
          <RadioButtonGroup name={props.questionKey} columns={props.columns}>
            {options}
          </RadioButtonGroup>
        ),
        initialValues: {
          ...(previousAnswer ? { [props.questionKey]: String(previousAnswer) } : {}),
          ...(props.textInputQuestionKey
            ? {
                [props.textInputQuestionKey]: String(
                  (props.quiz as Record<string, unknown>)?.[props.textInputQuestionKey] ?? '',
                ),
              }
            : {}),
        },
        validationSchema: Yup.object({
          [props.questionKey]: Yup.string().required('Please select an option'),
        }),
      }}
      onNextClick={props.onNextClick}
      onBackClick={props.hideBackLink ? undefined : props.onBackClick}
      goalAnswersToBeSubmitted={props.goalAnswersToBeSubmitted}
    />
  )
}
